import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Loader2, ShieldCheck, Building2, Mail, Phone, User } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

const CARGO_TYPES = ["Cash in Transit", "Gold & Bullion", "Fine Art", "High-Tech Components", "Other"];
const SECURITY_LEVELS = [
  { key: "Standard", desc: "Level III armor, single-crew, GPS-tracked" },
  { key: "Enhanced", desc: "Level V armor, two-crew, escort vehicle" },
  { key: "Maximum", desc: "Level VII armor, armed detail, encrypted telemetry" },
  { key: "Sovereign", desc: "Diplomatic pouch, marshaled aircraft, black-out routing" },
];

export default function Contact() {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    cargo_type: "Cash in Transit", origin_country: "", destination_country: "",
    security_level: "Enhanced", estimated_value: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", form);
      toast.success(data.message || "Quote request submitted");
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
        <ShieldCheck className="w-16 h-16 text-[#D4AF37] mx-auto" />
        <h1 className="mt-8 font-display text-4xl font-bold text-[#F0F4F8]">Request Received</h1>
        <p className="mt-6 text-[#9EABC0] max-w-lg mx-auto">
          Your quote request has been encrypted and delivered to our secure operations desk.
          A dedicated relationship manager will contact you within <span className="text-[#D4AF37]">24 hours</span>.
        </p>
        <div className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#9EABC0]">
          Reference · AGL-INTAKE-{Date.now().toString().slice(-6)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionLabel>Secure Intake</SectionLabel>
          <h1 className="mt-6 font-display font-black uppercase text-5xl sm:text-6xl text-[#F0F4F8] leading-[0.95]">
            Request a <span className="text-[#D4AF37]">custom quote.</span>
          </h1>
          <p className="mt-6 text-[#9EABC0] leading-relaxed">
            Corporate consignments only. All submissions pass through a hardened enclave and are visible only to
            a two-person cleared operations pair.
          </p>

          <div className="mt-10 space-y-6">
            {[
              { icon: ShieldCheck, t: "End-to-end encryption", d: "TLS 1.3 + AES-256 at rest" },
              { icon: Building2, t: "Corporate only", d: "We do not accept individual retail requests" },
              { icon: Mail, t: "24-hour response SLA", d: "Cleared operator will make first contact" },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="flex items-start gap-4">
                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-display font-semibold text-[#F0F4F8]">{t}</div>
                  <div className="text-sm text-[#9EABC0]">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="quote-form"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-7 border border-[#A1AEC2]/20 bg-[#0A1024] p-8 lg:p-10 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field icon={Building2} label="Company Name" required>
              <input required data-testid="q-company" value={form.company_name} onChange={e => upd("company_name", e.target.value)} className={inputCls} />
            </Field>
            <Field icon={User} label="Contact Name" required>
              <input required data-testid="q-contact" value={form.contact_name} onChange={e => upd("contact_name", e.target.value)} className={inputCls} />
            </Field>
            <Field icon={Mail} label="Corporate Email" required>
              <input required type="email" data-testid="q-email" value={form.email} onChange={e => upd("email", e.target.value)} className={inputCls} />
            </Field>
            <Field icon={Phone} label="Phone">
              <input data-testid="q-phone" value={form.phone} onChange={e => upd("phone", e.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Cargo Type" required>
              <select data-testid="q-cargo" value={form.cargo_type} onChange={e => upd("cargo_type", e.target.value)} className={inputCls}>
                {CARGO_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Estimated Value">
              <input data-testid="q-value" value={form.estimated_value} onChange={e => upd("estimated_value", e.target.value)} placeholder="e.g. $10M USD" className={inputCls} />
            </Field>
            <Field label="Origin Country" required>
              <input required data-testid="q-origin" value={form.origin_country} onChange={e => upd("origin_country", e.target.value)} placeholder="Switzerland" className={inputCls} />
            </Field>
            <Field label="Destination Country" required>
              <input required data-testid="q-destination" value={form.destination_country} onChange={e => upd("destination_country", e.target.value)} placeholder="Singapore" className={inputCls} />
            </Field>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Required Security Level</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SECURITY_LEVELS.map(l => {
                const active = form.security_level === l.key;
                return (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => upd("security_level", l.key)}
                    data-testid={`q-sec-${l.key.toLowerCase()}`}
                    className={`text-left p-4 border transition-all ${active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#A1AEC2]/25 hover:border-[#D4AF37]/50"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-display font-bold ${active ? "text-[#D4AF37]" : "text-[#F0F4F8]"}`}>{l.key}</span>
                      {active && <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />}
                    </div>
                    <div className="mt-1 text-xs text-[#9EABC0]">{l.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Additional Details">
            <textarea data-testid="q-message" value={form.message} onChange={e => upd("message", e.target.value)} rows={4} placeholder="Timing, sensitivities, insurance requirements…" className={`${inputCls} resize-none`} />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="q-submit"
            className="w-full bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest py-4 hover:bg-[#C5A059] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Transmit Secure Request
          </button>
        </motion.form>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#060C1F] border border-[#A1AEC2]/30 text-[#F0F4F8] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all rounded-none px-4 py-3 text-sm outline-none";

function Field({ icon: Icon, label, required, children }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />}
        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">
          {label}{required && <span className="text-[#D4AF37] ml-1">*</span>}
        </label>
      </div>
      {children}
    </div>
  );
}
